import { useEffect, useState } from 'react';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import { createCloudWorkspaceClient } from '@/lib/cloud-runtime/cloud-workspace-client';
import { errorMessage } from '@/lib/error-message';
import type { WorkspaceClient } from '@/lib/workspaces/workspace-client';

/**
 * Owns the cloud workspace client bound to the active {@link CloudRuntime}:
 * creates one when a runtime appears, waits for it to become ready, and tears it
 * down (clearing the state only if it still points at this client) on change or
 * unmount. Connection failures are reported through `onError`. Extracted
 * verbatim from the inline effect in `SidePanel`.
 */
export function useCloudWorkspaceClient(
  cloudRuntime: CloudRuntime | null,
  onError: (message: string) => void,
): WorkspaceClient | null {
  const [cloudClient, setCloudClient] = useState<WorkspaceClient | null>(null);
  useEffect(() => {
    if (cloudRuntime === null) {
      setCloudClient(null);
      return;
    }
    const client = createCloudWorkspaceClient(cloudRuntime);
    let disposed = false;
    void client.ready.then(
      () => {
        if (!disposed) setCloudClient(client);
      },
      (error: unknown) => {
        if (!disposed) onError(errorMessage(error));
      },
    );
    return () => {
      disposed = true;
      setCloudClient((current) => (current === client ? null : current));
      client.close();
    };
  }, [cloudRuntime, onError]);
  return cloudClient;
}
